/**
 * Política de sesión en cliente — US-69 (contrato §4 SESSION_POLICY).
 *
 * El refresh silencioso del access token (src/auth/cognito.js) mantiene viva la
 * credencial, pero por sí solo NO cubre las dos expiraciones que pide el
 * contrato §4:
 *   - idle timeout     : inactividad del usuario  -> re-login
 *   - absolute timeout : vida máxima de la sesión -> re-login
 *
 * Este módulo aporta esas dos. Los tiempos los fija el tenant vía
 * `descriptor.SESSION_POLICY`; si no llegan, se usan los defaults recomendados
 * del contrato. Pórtico NO decide la política, solo la aplica (R-PT-01).
 *
 * El estado autoritativo sigue en el tenant (R-PT-09): esto solo decide cuándo
 * el cliente deja de confiar en la sesión local y pide volver a entrar.
 */

// Defaults recomendados del contrato §4 (ajustables por el tenant).
export const SESSION_POLICY_DEFAULTS = {
  access_ttl_min: 60,
  idle_timeout_min: 60,
  absolute_timeout_h: 8,
  refresh: 'silent-rebootstrap',
}

// Interacciones que cuentan como "actividad" para reiniciar el idle.
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart']

function numberOr(v, fallback) {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback
}

// Normaliza la política cruda del descriptor a valores usables, con defaults
// para cualquier campo ausente o inválido.
export function resolvePolicy(raw) {
  const p = raw ?? {}
  return {
    access_ttl_min:     numberOr(p.access_ttl_min,     SESSION_POLICY_DEFAULTS.access_ttl_min),
    idle_timeout_min:   numberOr(p.idle_timeout_min,   SESSION_POLICY_DEFAULTS.idle_timeout_min),
    absolute_timeout_h: numberOr(p.absolute_timeout_h, SESSION_POLICY_DEFAULTS.absolute_timeout_h),
    refresh:            p.refresh ?? SESSION_POLICY_DEFAULTS.refresh,
  }
}

/**
 * Arranca los temporizadores de expiración de sesión. Devuelve una función de
 * parada IDEMPOTENTE que limpia timers y listeners (segura de llamar varias
 * veces). Cada callback se dispara como mucho una vez: al expirar, se paran los
 * temporizadores antes de invocarlo.
 *
 * @param {object}    opts
 * @param {object=}   opts.policy      SESSION_POLICY del descriptor (cruda)
 * @param {string=}   opts.issuedAt    ISO de inicio de sesión (descriptor.session.issued_at)
 * @param {Function=} opts.onIdle      se llama al expirar por inactividad
 * @param {Function=} opts.onAbsolute  se llama al expirar por vida máxima
 * @returns {Function} stop()
 */
export function startSessionTimers({ policy, issuedAt, onIdle, onAbsolute } = {}) {
  const { idle_timeout_min, absolute_timeout_h } = resolvePolicy(policy)
  const idleMs = idle_timeout_min * 60_000
  const absMs  = absolute_timeout_h * 3_600_000

  // La vida máxima se mide desde que el KERNEL inició la sesión (issued_at), no
  // desde este montaje: así un re-bootstrap silencioso no "resetea" el tope
  // absoluto. Si no hay issued_at válido, se cuenta desde ahora.
  const base = Date.parse(issuedAt)
  const absoluteDeadline = (Number.isNaN(base) ? Date.now() : base) + absMs

  let idleTimer = null
  let absTimer = null
  let stopped = false

  function stop() {
    if (stopped) return
    stopped = true
    clearTimeout(idleTimer)
    clearTimeout(absTimer)
    for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, resetIdle)
  }

  function resetIdle() {
    if (stopped) return
    clearTimeout(idleTimer)
    idleTimer = setTimeout(() => { stop(); onIdle?.() }, idleMs)
  }

  // El deadline absoluto puede haber pasado ya (sesión vieja rehidratada):
  // en ese caso expira en el próximo tick, no de inmediato dentro del arranque.
  const remaining = Math.max(absoluteDeadline - Date.now(), 0)
  absTimer = setTimeout(() => { stop(); onAbsolute?.() }, remaining)

  for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, resetIdle, { passive: true })
  resetIdle()

  return stop
}
